import { Injectable } from '@angular/core';

import { EnableClassTelemetry } from 'app/services/otel.service';

import { APIService, Headers, QueryParams } from './api.service';
export interface SideloadedStorageListResult<T> extends StorageListResult<T> {
  sideloaded?: any;
}

export interface StorageListResult<T> {
  nextPageToken?: string;
  count?: number;
  pageSize?: number;
  items?: T[];
  request?: StoragePageRequest<T>;
  [key: string]: any;
}

export interface StoragePageRequest<T> {
  prev?: StorageListResult<T> | any;
  endpoint: string;
  params?: QueryParams;
  headers?: Headers;
}

export interface StorageError {
  [httpCode: number]: StorageErrorEntry;
}

export interface StorageErrorEntry {
  error_code: string;
  reference_id: string;
  error: string;
}

@EnableClassTelemetry()
@Injectable()
export abstract class StorageAPIService extends APIService {
  public static readonly MAX_LIMIT = 10000;

  getBaseUrl() {
    const appConfig = (window as any).appConfig || {};
    if (appConfig.blender && appConfig.blender.url) {
      return appConfig.blender.url;
    }
    return 'http://localhost:8080';
  }

  getBaseHeaders() {
    return {
      Authorization: this.getToken(),
    };
  }

  public async postPage<T>(
    endpoint: string,
    body?: QueryParams,
    headers?: Headers,
    queryParams?: QueryParams
  ): Promise<StorageListResult<T>> {
    const result = await this.post<StorageListResult<T>>(endpoint, body, headers, queryParams);

    result.request = { endpoint, params: body, headers };
    return result;
  }

  public async getPage<T>(
    endpoint: string,
    params?: QueryParams,
    headers?: Headers
  ): Promise<SideloadedStorageListResult<T>> {
    const result = await this.get<StorageListResult<T>>(endpoint, params, headers);

    result.request = { endpoint, params, headers };
    return result;
  }

  public getPrevPage<T>(page: StorageListResult<T>): Promise<StorageListResult<T>> {
    if (!page.request.prev) {
      return Promise.reject(new Error('already at first page'));
    }

    return this.get<StorageListResult<T>>(
      page.request.prev.endpoint,
      page.request.prev.params,
      page.request.prev.headers
    ).then((result) => {
      return {
        ...result,
        request: {
          prev: page.request.prev.prev,
          endpoint: page.request.prev.endpoint,
          params: page.request.prev.params,
          headers: page.request.prev.headers,
        },
      };
    });
  }

  public getNextPage<T>(page: StorageListResult<T>): Promise<StorageListResult<T>> {
    if (!page.request) {
      return Promise.reject(new Error(`page request wasn't created through getPage`));
    }

    if (!page.nextPageToken) {
      return Promise.reject(new Error('already at last page'));
    }

    const params = { 'page-token': page.nextPageToken };

    return this.get<StorageListResult<T>>(page.request.endpoint, params, page.request.headers).then(
      (result) => {
        return {
          ...result,
          request: {
            prev: page.request,
            endpoint: page.request.endpoint,
            params,
            headers: page.request.headers,
          },
        };
      }
    );
  }
}
